import { MultiSelect, Select, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { rangePresets } from "../../helpers/dates";
import { getFullName } from "../../helpers/stringHelper";
import type { Coach } from "../../models/coach";
import { type CompetenceCategory, getCompetenceCategoryOptions } from "../../models/enums/competenceCategory";
import { getImpressionOptions, type Impression } from "../../models/enums/impression";
import { FilterSection } from "../filter/FilterSection";
import { FilterSelect } from "../select/FilterSelect";

export type ApprenticeObservationsFilterType = {
  sort_key?: "created_by" | "observed_at";
  sort_direction?: "asc" | "desc";
  created_by?: string;
  text?: string;
  competence_categories?: CompetenceCategory[];
  range?: string[];
  impression?: Impression;
};

type ApprenticeObservationsFilterProps = {
  filter: ApprenticeObservationsFilterType;
  onFilterChange: (values: ApprenticeObservationsFilterType) => void;
  filterableCoaches: Coach[];
};

const resetFilters: ApprenticeObservationsFilterType = {
  created_by: undefined,
  text: undefined,
  competence_categories: undefined,
  range: undefined,
  impression: undefined,
};

export function ApprenticeObservationsFilter({ filter, onFilterChange, filterableCoaches }: ApprenticeObservationsFilterProps) {
  const filterForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      created_by: filter.created_by,
      text: filter.text,
      competence_categories: filter.competence_categories ?? [],
      range: filter.range?.[0] && filter.range[1] ? [filter.range[0], filter.range[1]] : [],
      impression: filter.impression,
    },
  });

  const createdByOptions = filterableCoaches.map((coach) => ({
    label: getFullName(coach),
    value: coach.id.toString(),
  }));

  return (
    <FilterSection
      form={filterForm}
      onSubmit={onFilterChange}
      onReset={() => onFilterChange(resetFilters)}
      filters={[
        <FilterSelect
          clearable
          data={createdByOptions}
          label="Erstellt durch"
          key={filterForm.key("created_by")}
          {...filterForm.getInputProps("created_by")}
        />,
        <TextInput label="Text" key={filterForm.key("text")} {...filterForm.getInputProps("text")} />,
        <MultiSelect
          label="Kompetenzen"
          data={getCompetenceCategoryOptions()}
          key={filterForm.key("competence_categories")}
          {...filterForm.getInputProps("competence_categories")}
        />,
        <DatePickerInput
          clearable
          valueFormat="DD.MM.YYYY"
          label="Erstellt zwischen"
          type="range"
          presets={rangePresets()}
          key={filterForm.key("range")}
          {...filterForm.getInputProps("range")}
        />,
        <Select
          clearable
          data={getImpressionOptions()}
          label="Eindruck"
          key={filterForm.key("impression")}
          {...filterForm.getInputProps("impression")}
        />,
      ]}
    />
  );
}
